import { useEffect, useMemo, useRef, useState } from 'react'
import { Floating } from '../shell/floating.js'
import { Clock, DoorOpen, Megaphone, Music, ShieldAlert, Ticket, TriangleAlert } from 'lucide-react'
import { useSoiree } from './SoireeData.js'
import { NowMarker, heureFr, useNowMinute } from './lib.js'
import {
  buildSummary, clusterPoints, couchesDeLaBarre, densityBars, fracOf, hourTicks, parseBounds, stackRanges,
  type TlCluster, type TlPointIn, type TlRangeRow,
} from './timeline-model.js'
import { couleurCss } from './analytics-nuit-model.js'

/**
 * TIMELINE OPÉRATIONNELLE V2 — la nuit comme un AXE.
 *
 * L'ancienne tuile était une liste de lignes horodatées ; celle-ci pose les
 * événements sur une échelle temporelle RÉELLE : un incident à 01:36 et une
 * annonce à 03:58 sont éloignés proportionnellement, et le regard comprend
 * passé → Maintenant → suite de la nuit sans rien ouvrir.
 *
 * Composition (de haut en bas) :
 *   résumé d'une ligne · axe horaire + jalons · plages (Main Courante,
 *   salles, alertes avec leur DURÉE) · densité des scans.
 *
 * TOUT est positionné en % de l'axe — géométrie calculée par
 * `timeline-model.ts`, testée à part. Le conteneur défile horizontalement
 * quand la tuile est étroite (mobile compris), avec ouverture sur
 * « Maintenant » pendant la nuit.
 *
 * INVARIANT : billets scannés ≠ personnes présentes. Aucun libellé de ce
 * composant ne parle de « présents ».
 */

const POINT_ICONS: Record<string, typeof Ticket> = {
  OPENING: DoorOpen, FIRST_SCAN: Ticket, LAST_SCAN: Ticket,
  CLOSING_ANNOUNCEMENT: Megaphone, CANCELLED: TriangleAlert, MC_GRAVE_ENTRY: ShieldAlert,
}

interface TimelinePayload {
  phase: 'UPCOMING' | 'ONGOING' | 'FINISHED' | 'CANCELLED'
  generated_at: string
  bounds: { start: string; end: string }
  density: { bucket_s: number; buckets: { t: string; scans: number; cumulative: number; par?: Record<string, number> }[]
    peak: { t: string; scans: number } | null
    /** Toute la nuit (19/10/2026) : les billetteries empilées ; absent ou vide = la soirée seule. */
    billetteries?: { key: string; label: string; color: string }[] }
  points: TlPointIn[]
  ranges: { id: string; kind: string; start: string; end: string | null; label: string
    severity: 'NEUTRAL' | 'WARNING' | 'CRITICAL' }[]
  last_scan_at: string | null
  scans_total: number
  mc_available: boolean
}

/* Largeur minimale de l'axe : en dessous, les heures deviennent illisibles.
   La tuile plus étroite (ou le mobile) obtient un défilement horizontal. */
const MIN_PX_PER_HOUR = 88

const DENSITY_W = 1000
const DENSITY_H = 34

export function OperationalTimelineWidget() {
  const { id, detail } = useSoiree()
  const [data, setData] = useState<TimelinePayload | null>(null)

  useEffect(() => {
    let vivant = true
    const charger = () => {
      fetch(`/api/v1/soirees/${id}/operational-timeline`, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : null))
        .then((j: TimelinePayload | null) => { if (vivant && j) setData(j) })
        .catch(() => undefined)
    }
    charger()
    // Même cadence que les autres tuiles de la page — pas de canal dédié.
    const t = setInterval(charger, 60_000)
    return () => { vivant = false; clearInterval(t) }
  }, [id])

  /* Horloge du curseur Maintenant — la MÊME que celle d'Analytics, pour que
     les deux représentations du même axe ne désignent pas deux instants. */
  const nowMs = useNowMinute(data?.phase === 'ONGOING')

  if (!data) {
    return (
      <section className="lc-panel gd-panel so-widget" aria-label="Timeline de la soirée">
        <header className="lc-panel__head">
          <h2 className="lc-section-title"><Clock size={16} aria-hidden="true" /> Timeline</h2>
        </header>
        <div className="lc-panel__body"><p className="lc-hint">Chargement…</p></div>
      </section>
    )
  }
  return <TimelineBody data={data} nowMs={nowMs} freshness={detail?.freshness ?? null} />
}

function TimelineBody({ data, nowMs, freshness }: {
  data: TimelinePayload
  nowMs: number
  freshness: { state: string; seconds_since_success: number | null } | null
}) {
  const bounds = useMemo(() => parseBounds(data.bounds), [data.bounds])
  const ongoing = data.phase === 'ONGOING'
  const nowFrac = ongoing ? fracOf(nowMs, bounds) : null

  const ticks = useMemo(() => hourTicks(bounds), [bounds])
  const clusters = useMemo(() => clusterPoints(data.points, bounds), [data.points, bounds])
  const rangeRows = useMemo(() => stackRanges(data.ranges, bounds, nowMs), [data.ranges, bounds, nowMs])
  const nRows = rangeRows.reduce((m, r) => Math.max(m, r.row + 1), 0)

  const { bars, max: densMax } = useMemo(
    () => densityBars(data.density.buckets, bounds, data.density.bucket_s, DENSITY_W, DENSITY_H),
    [data.density, bounds])
  const pasMin = Math.round(data.density.bucket_s / 60)
  /* TOUTE LA NUIT : chaque barre empile ses billetteries, chacune dans sa
     couleur (réglage). Le pic est celui de la nuit : 121 entrées à 01:00 le
     15/10, quand Shotgun seul en montrait 104. */
  const billetteries = data.density.billetteries ?? []
  const empile = billetteries.length > 1
  const ordre = billetteries.map((b) => b.key)

  const summary = useMemo(() => buildSummary({
    phase: data.phase, boundsStartMs: bounds.startMs, scansTotal: data.scans_total,
    peakT: data.density.peak?.t ?? null, lastScanAt: data.last_scan_at,
    alertRanges: data.ranges.filter((r) => r.kind === 'ALERT'),
  }, nowMs, heureFr), [data, bounds, nowMs])

  /* ─── lecture au survol de la densité ─── */
  const [hover, setHover] = useState<number | null>(null)
  const plotRef = useRef<HTMLDivElement>(null)
  const ancreDensite = useRef<HTMLSpanElement>(null)
  const surDensite = (clientX: number) => {
    const el = plotRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const frac = Math.min(1, Math.max(0, (clientX - r.left) / Math.max(r.width, 1)))
    const tMs = bounds.startMs + frac * (bounds.endMs - bounds.startMs)
    let best: number | null = null
    for (let i = 0; i < data.density.buckets.length; i++) {
      const t0 = new Date(data.density.buckets[i]!.t).getTime()
      if (tMs >= t0 && tMs < t0 + data.density.bucket_s * 1000) { best = i; break }
    }
    setHover(best)
  }

  /* ─── ouverture sur Maintenant quand l'axe défile ───
     Une seule fois : recentrer en boucle interdirait tout retour manuel. */
  const scrollRef = useRef<HTMLDivElement>(null)
  const centered = useRef(false)
  useEffect(() => {
    if (centered.current || nowFrac === null) return
    const el = scrollRef.current
    if (!el || el.scrollWidth <= el.clientWidth + 4) return
    el.scrollLeft = nowFrac * el.scrollWidth - el.clientWidth * 0.6
    centered.current = true
  }, [nowFrac])

  const spanH = (bounds.endMs - bounds.startMs) / 3_600_000
  const minWidth = Math.round(spanH * MIN_PX_PER_HOUR)

  const hovered = hover === null ? null : data.density.buckets[hover] ?? null
  const hoveredFrac = hovered === null ? 0
    : fracOf(new Date(hovered.t).getTime() + data.density.bucket_s * 500, bounds)

  const fresh = freshness && freshness.state === 'HEALTHY' && freshness.seconds_since_success !== null
  const upcoming = data.phase === 'UPCOMING'

  return (
    <section className="lc-panel gd-panel so-widget" aria-label="Timeline de la soirée">
      <header className="lc-panel__head so-otl__head">
        <h2 className="lc-section-title"><Clock size={16} aria-hidden="true" /> Timeline</h2>
        {/* LIVE n'est affiché que si la donnée est réellement fraîche. */}
        {ongoing && fresh ? (
          <span className="so-quality so-quality--live">
            <span className="lc-dot lc-dot--ok lc-dot--live" />
            LIVE · {freshness!.seconds_since_success} s
          </span>
        ) : null}
      </header>

      <div className="lc-panel__body so-otl">
        <p className="so-otl__summary">
          {summary.map((s, i) => (
            <span key={i}>{i > 0 ? <span className="so-otl__sep" aria-hidden="true"> · </span> : null}{s}</span>
          ))}
        </p>

        {upcoming && data.scans_total === 0 && data.ranges.length === 0 ? (
          /* Rien ne s'est encore produit : pas d'immense axe vide, et surtout
             pas une chronologie future fabriquée. */
          <p className="lc-hint so-otl__empty">
            La chronologie s’écrira à l’ouverture de la nuit.
          </p>
        ) : (
          <div className="so-otl__scroll" ref={scrollRef}>
            <div className="so-otl__canvas" style={{ minWidth: `${minWidth}px` }}>

              {/* ═══ axe horaire ═══ */}
              <div className="so-otl__axis" aria-hidden="true">
                {ticks.map((t) => (
                  <span
                    className={`so-otl__tick${t.frac > 0.985 ? ' so-otl__tick--end' : ''}${t.frac < 0.015 ? ' so-otl__tick--start' : ''}`}
                    key={t.tMs} style={{ left: `${t.frac * 100}%` }}>
                    <i />{heureFr(new Date(t.tMs).toISOString())}
                  </span>
                ))}
              </div>

              {/* ═══ jalons ═══ */}
              <div className="so-otl__points">
                {clusters.map((c) => <PointMarker key={c.items[0]!.id} cluster={c} />)}
              </div>

              {/* ═══ plages (durées réelles) ═══ */}
              {nRows > 0 ? (
                <div className="so-otl__ranges" style={{ height: `${nRows * 17}px` }}>
                  {rangeRows.map((r) => <RangeBar key={r.id} r={r} nowMs={nowMs} />)}
                </div>
              ) : null}

              {/* ═══ densité des scans ═══ */}
              <div
                className="so-otl__density" ref={plotRef}
                onPointerMove={(e) => surDensite(e.clientX)}
                onPointerLeave={() => setHover(null)}
                role="img"
                aria-label={`Billets scannés par tranche de ${Math.round(data.density.bucket_s / 60)} minutes — les sorties ne sont pas comptabilisées`}
              >
                <svg viewBox={`0 0 ${DENSITY_W} ${DENSITY_H}`} preserveAspectRatio="none">
                  {bars.map((b) => (b.h <= 0 ? null : empile ? (
                    <g key={b.index} className={`so-otl__pile${hover === b.index ? ' so-otl__pile--on' : ''}`}>
                      {couchesDeLaBarre(b, data.density.buckets[b.index]?.par, ordre).map((c) => (
                        <rect key={c.key} x={b.x} y={c.y} width={b.w} height={c.h}
                          style={{ fill: couleurCss(billetteries.find((x) => x.key === c.key)?.color) ?? 'var(--cyan)' }} />
                      ))}
                    </g>
                  ) : (
                    <rect key={b.index} x={b.x} y={b.y} width={b.w} height={b.h}
                      className={`so-otl__bar${hover === b.index ? ' so-otl__bar--on' : ''}`} />
                  )))}
                </svg>
                {empile ? (
                  <span className="so-otl__legende" aria-hidden="true">
                    {billetteries.map((x) => (
                      <span key={x.key}><i style={{ background: couleurCss(x.color) ?? 'var(--cyan)' }} />{x.label}</span>
                    ))}
                  </span>
                ) : null}
                {/* ÉCHELLE : une barre doit pouvoir se chiffrer. Deux mots en
                    haut à droite valent mieux qu'un axe Y complet — on veut
                    savoir si un pic vaut 3 ou 30, pas lire une graduation. */}
                {densMax > 0 ? (
                  <span className="so-otl__scale">max {densMax} / {pasMin} min</span>
                ) : null}
                {data.density.peak ? (
                  <span className="so-otl__peak" style={{
                    left: `${fracOf(new Date(data.density.peak.t).getTime() + data.density.bucket_s * 500, bounds) * 100}%`,
                  }}>
                    Pic {heureFr(data.density.peak.t)}
                  </span>
                ) : null}
                {hovered ? (
                  <>
                    <span className="so-otl__guide" style={{ left: `${hoveredFrac * 100}%` }} aria-hidden="true" />
                    {/* Ancre invisible : `Floating` a besoin d'un élément à
                        mesurer, et le curseur n'en est pas un. */}
                    <span ref={ancreDensite} className="so-otl__anchor"
                      style={{ left: `${hoveredFrac * 100}%` }} aria-hidden="true" />
                    <Floating open anchorRef={ancreDensite} placement="top-start"
                      className="so-tip so-otl__float">
                      <p className="so-tip__t">
                        {heureFr(hovered.t)} → {heureFr(new Date(new Date(hovered.t).getTime() + data.density.bucket_s * 1000).toISOString())}
                      </p>
                      <ul className="so-tip__list">
                        {empile ? billetteries.map((x) => (
                          <li key={x.key}><span className="so-tip__dot" style={{ background: couleurCss(x.color) ?? 'var(--cyan)' }} aria-hidden="true" />
                            <span className="so-tip__label">{x.label}</span>
                            <span className="so-tip__val">{hovered.par?.[x.key] ?? 0}</span></li>
                        )) : null}
                        <li><span className="so-tip__dot" style={{ background: empile ? 'var(--text-2)' : 'var(--cyan)' }} aria-hidden="true" />
                          <span className="so-tip__label">{empile ? 'Billets scannés, toute la nuit' : 'Billets scannés'}</span>
                          <span className="so-tip__val">{hovered.scans}</span></li>
                        <li><span className="so-tip__dot" style={{ background: 'var(--violet)' }} aria-hidden="true" />
                          <span className="so-tip__label">Cumul</span>
                          <span className="so-tip__val">{hovered.cumulative}</span></li>
                        {/* Rythme = projection du volume de l'intervalle sur une
                            heure, PAS un volume observé sur une heure. Le
                            libellé le dit, pour qu'on ne le lise pas comme tel. */}
                        <li><span className="so-tip__dot" style={{ background: 'var(--text-3)' }} aria-hidden="true" />
                          <span className="so-tip__label">Rythme sur l’intervalle</span>
                          <span className="so-tip__val">{Math.round(hovered.scans * 3600 / data.density.bucket_s)}/h</span></li>
                      </ul>
                    </Floating>
                  </>
                ) : null}
              </div>

              {/* ═══ Maintenant ═══ (primitive partagée avec Analytics) */}
              <NowMarker frac={nowFrac} variant="timeline" />
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

/** Jalon (ou grappe de jalons proches) — un bouton : accessible au clavier,
 *  le tooltip s'ouvre au survol ET au focus. */
function PointMarker({ cluster }: { cluster: TlCluster }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)
  const solo = cluster.items.length === 1 ? cluster.items[0]! : null
  const Icone = solo ? (POINT_ICONS[solo.kind] ?? Music) : null
  const severite = cluster.items.some((i) => i.severity === 'CRITICAL') ? 'critical'
    : cluster.items.some((i) => i.severity === 'WARNING') ? 'warning' : 'neutral'
  const texte = cluster.items
    .map((i) => `${heureFr(i.t)} — ${i.label}`).join(' · ')
  return (
    <span className="so-otl__pt" style={{ left: `${cluster.frac * 100}%` }}>
      <button
        ref={ref}
        type="button"
        className={`so-otl__marker so-otl__marker--${severite}`}
        aria-label={texte}
        onPointerEnter={() => setOpen(true)} onPointerLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
      >
        {Icone ? <Icone size={11} aria-hidden="true" /> : <span className="so-otl__count">{cluster.items.length}</span>}
      </button>
      {/* Portalisé vers <body> : trois conteneurs à `overflow` le
          découpaient — le panneau, son corps et le viewport défilant. La
          primitive gère aussi le basculement haut/bas et le rabattement
          latéral, et suit le défilement horizontal. */}
      <Floating open={open} anchorRef={ref} placement="top-start"
        className="so-tip so-otl__float">
        <ul className="so-tip__list so-tip__list--plain">
          {cluster.items.map((i) => (
            <li key={i.id}>
              <span className="so-tip__label">{i.label}</span>
              <span className="so-tip__val">{heureFr(i.t)}</span>
            </li>
          ))}
        </ul>
      </Floating>
    </span>
  )
}

const RANGE_LABEL: Record<string, string> = { MAIN_COURANTE: 'Main Courante', ROOM: 'Salle', ALERT: 'Alerte' }

/** Une DURÉE : segment aux extrémités nettes ; extrémité ouverte quand la
 *  plage court encore (elle rejoint Maintenant). */
function RangeBar({ r, nowMs }: { r: TlRangeRow; nowMs: number }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)
  const durMs = (r.active ? nowMs : new Date(r.end!).getTime()) - new Date(r.start).getTime()
  const dur = durMs >= 3_600_000
    ? `${Math.floor(durMs / 3_600_000)} h ${String(Math.round((durMs % 3_600_000) / 60_000)).padStart(2, '0')}`
    : `${Math.max(1, Math.round(durMs / 60_000))} min`
  const kindLabel = RANGE_LABEL[r.kind] ?? r.kind
  // « Main Courante — Main Courante » : le type ne se répète pas quand le
  // libellé est déjà lui.
  const aria = `${kindLabel === r.label ? r.label : `${kindLabel} — ${r.label}`} — ${heureFr(r.start)}`
    + (r.active ? ` — en cours depuis ${dur}` : ` → ${heureFr(r.end!)} — durée ${dur}`)
  return (
    <span className="so-otl__range-wrap" style={{
      left: `${r.startFrac * 100}%`, width: `${(r.endFrac - r.startFrac) * 100}%`, top: `${r.row * 17}px`,
    }}>
      <button
        ref={ref}
        type="button"
        className={`so-otl__range so-otl__range--${r.kind.toLowerCase()} so-otl__range--${r.severity.toLowerCase()}${r.active ? ' so-otl__range--active' : ''}`}
        aria-label={aria}
        onPointerEnter={() => setOpen(true)} onPointerLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
      >
        {/* Le nom À MÊME la plage : sans lui, deux longues barres restaient
            muettes tant qu'on ne les survolait pas. Il s'efface tout seul par
            ellipsis quand la plage est trop courte pour le porter. */}
        <span className="so-otl__range-name">{r.label}</span>
      </button>
      <Floating open={open} anchorRef={ref} placement="top-start"
        className="so-tip so-otl__float">
          <p className="so-tip__t">{r.label}</p>
          <ul className="so-tip__list so-tip__list--plain">
            <li><span className="so-tip__label">{r.active ? 'Depuis' : 'Période'}</span>
              <span className="so-tip__val">
                {heureFr(r.start)}{r.active ? '' : ` → ${heureFr(r.end!)}`}
              </span></li>
            <li><span className="so-tip__label">{r.active ? 'En cours depuis' : 'Durée'}</span>
              <span className="so-tip__val">{dur}</span></li>
          </ul>
      </Floating>
    </span>
  )
}
